import * as dateFns from 'date-fns';
import ru from 'date-fns/locale/ru';
import en from 'date-fns/locale/en-US';
import { EmptyValue, IfNotEmpty } from '../types';
export const DefaultLocale = 'ru';

export const SecondInterval = 1000;
export const MinuteInterval = SecondInterval * 60;
export const HourInterval = MinuteInterval * 60;
export const DayInterval = HourInterval * 24;

const AllowedLocales = { ru, en };
const planTimeZoneOffset = new Date().getTimezoneOffset() * -1 / 60;

export type AllowedLocale = keyof (typeof AllowedLocales);

export type PossibleDateType = Date | number | string;

export const IsoFormat = 'yyyy-MM-dd\'T\'HH:mm:ss';

export type Period = {
    start: number | Date,
    end: number | Date,
}

export const format = <T extends EmptyValue | PossibleDateType>(
    strDate: T,
    format: string,
    removeDots: boolean = false,
    locale: AllowedLocale = DefaultLocale,
): IfNotEmpty<T, string> => {
    if (!strDate) {
        return strDate as IfNotEmpty<T, string>;
    }

    const date = new Date(strDate);
    let dateResult = dateFns.format(date, format, { locale: AllowedLocales[locale] })
    if (removeDots)
        dateResult = dateResult.split('.').join('');

    return dateResult as IfNotEmpty<T, string>;
};

const setTimeZoneOffset = <T extends Date | null | undefined>(date: T, zoneOffset: number): T => {
    if (!date) return date;
    date.setTime(date.getTime() + zoneOffset * HourInterval);
    return date;
}

export const formatDate = (
    date: Date | null | undefined,
    format: string,
    locale: AllowedLocale = DefaultLocale,
): string | null | undefined => {
    if (!date) return date;

    return dateFns.format(date, format, { locale: AllowedLocales[locale] });
};

export const getStartOfDay = <T extends Date | null | undefined>(date: T): T => {
    if (!date) return date;
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result as T;
}

export const getEndOfDay = <T extends Date | null | undefined>(date: T): T => {
    if (!date) return date;
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);
    return result as T;
}

type FormatDurationOptions = {
    withSec: boolean,
    hideZeroHours: boolean,
}

export const formatDuration = (ms: number, options: Partial<FormatDurationOptions> = {}): string => {
    const fullOptions: FormatDurationOptions = {
        withSec: true,
        hideZeroHours: true,
        ...options,
    }

    let diffTime = Math.floor(ms / 1000);

    if (diffTime === 0) {
        return fullOptions.hideZeroHours ? '00:00' : '00:00:00';
    }

    const hours = Math.floor(diffTime / 3600);
    diffTime %= 3600;
    const minutes = Math.floor(diffTime / 60);
    const seconds = diffTime % 60;

    const parts = fullOptions.withSec ? [hours, minutes, seconds] : [hours, minutes];

    const result = parts.map(p => String(p).padStart(2, '0')).join(':');
    return fullOptions.hideZeroHours ? result.replace('00:', '') : result;
};


export const getDurationStr = (start: Date, end: Date = new Date(), options?: Partial<FormatDurationOptions>) => {
    return formatDuration(Math.abs(end.getTime() - start.getTime()), options);
};

type TimeFormatOptions = {
    withSec: boolean,
    hideZeroHours: boolean,
    hideDays: boolean,
    hideZeroSeconds: boolean,
}

export const timeFormat = (
    ms: number,
    options: Partial<TimeFormatOptions> = {
        withSec: true,
        hideZeroHours: true,
        hideDays: true,
        hideZeroSeconds: true,
    },
): string => {
    if (ms < 1)
        return '-'

    let diffTime = ms / 1000;

    const days = Math.floor(diffTime / (3600 * 24));
    if (!options.hideDays) {
        diffTime %= (3600 * 24);
    }
    const hours = Math.floor(diffTime / 3600);
    diffTime %= 3600;
    const minutes = Math.floor(diffTime / 60);
    const seconds = diffTime % 60;

    const parts = [];

    if (days > 0 && !options.hideDays) {
        parts.push(`${days}д`)
    }

    if (hours > 0) {
        parts.push(`${hours} ч`);
    }

    if (!hours && !options.hideZeroHours) {
        parts.push(`00 ч`);
    }

    if (minutes > 0) {
        parts.push(`${minutes} мин`);
    }

    if (options.withSec && seconds > 0) {
        parts.push(`${parseFloat(seconds.toFixed(3))} с`);
    }

    if (options.withSec && !seconds && !options.hideZeroSeconds) {
        parts.push(`00 с`);
    }

    return parts.join(' ');
};

export const getDurationInDays = (startDate: Date | number, endDate: Date | number): number => {
    if (!startDate || !endDate)
        return 0;

    const interval = Math.abs(dateFns.differenceInHours(endDate, startDate)) / 24;

    return Math.ceil(interval)
}

//Возвращает список из дат, входящих в период
export const getDatesArrayByPeriod = (startDate: Date, endDate: Date): number[] => {
    let start = startDate.setHours(0, 0, 0, 0);
    const end = endDate.setHours(0, 0, 0, 0);
    let datesList: number[] = [];
    while (start <= end) {
        datesList.push(start);
        start = Number(dateFns.addDays(start, 1));
    }
    return datesList;
}

//Возвращает список из месяцев, входящих в период
export const getMonthArrayByPeriod = (startDate: Date, endDate: Date): { month: number, year: number }[] => {
    let startMonth = new Date(startDate).getMonth();
    const year = new Date(startDate).getFullYear();
    const datesList: number[] = getDatesArrayByPeriod(startDate, endDate);

    const monthList: any[] = [{ month: startMonth, year }];

    datesList.forEach(date => {
        const month = new Date(date).getMonth();
        const year = new Date(date).getFullYear();
        if (startMonth === month)
            return;

        startMonth = month;
        monthList.push({ month, year });

    })
    return monthList;
}

//Возвращает последний день месяца (числом)
//Номер месяца от 0 до 11
export const getLastDayOfMonth = (year: number, month: number): number => {
    if (!year || (!month && month !== 0))
        return 0;

    return new Date(year, month + 1, 0).getDate();
}

//Возвращает день недели числом от 0 до 6, где 0 - воскресенье
export const getDayOfWeek = (date: number): number => {
    if (!date)
        return 0;

    return dateFns.getDay(date);
}

//Получает строку в формате HH:mm:ss и преобразует ее в число секунд
export const getTimeFromStr = (timeStr: string): number => {
    if (!timeStr)
        return 0;

    const splitStr = timeStr.split(':').map(item => Number(item));

    return splitStr[0] * 3600 + splitStr[1] * 60 + splitStr[2];
}

//Выводит временной промежуток. Есть возможность задать формат для вывода промежутка.
//Если даты равны, то для даты начала и даты конца периода можно задать разный формат. Пример: 01 янв. 07:40 - 07:50
export const formatPeriod = (startDate: Date | string, endDate: Date | string, primaryFormat: string, sameDayFormat: string) => {
    const isSameDay = new Date(endDate).setHours(0, 0, 0, 0) === new Date(startDate).setHours(0, 0, 0, 0);

    return `${format(startDate, primaryFormat)} - ${format(endDate, isSameDay ? sameDayFormat : primaryFormat)}`;

}

//Возвращает первый день месяца
//По умолчанию возвращает дату с таймзоной, по опции можно возвращать дату без таймзоны
export const getFirstDateOfMonth = <T extends EmptyValue | PossibleDateType>(date: T, options: { withoutTimezone: boolean } = { withoutTimezone: false }): IfNotEmpty<T, Date> => {
    if (!date)
        return date as IfNotEmpty<T, Date>;

    const newDate = new Date(date);

    const year = newDate.getFullYear();
    const month = newDate.getMonth();

    let result: number | Date = new Date(year, month, 1);

    if (options.withoutTimezone) {
        result = Date.UTC(year, month, 1);
    }

    return new Date(result) as IfNotEmpty<T, Date>;
}

export const getLastDateOfMonth = <T extends EmptyValue | PossibleDateType>(date: T, options: { withoutTimezone: boolean } = { withoutTimezone: false }): IfNotEmpty<T, Date> => {
    if (!date)
        return date as IfNotEmpty<T, Date>;

    const year = new Date(date).getFullYear();
    const month = new Date(date).getMonth();
    const day = getLastDayOfMonth(year, month);

    let result: number | Date = new Date(year, month, day, 23, 59, 59);

    if (options.withoutTimezone) {
        result = Date.UTC(year, month, day, 23, 59, 59);
    }

    return new Date(result) as IfNotEmpty<T, Date>;
}

export const getDurationInSeconds = (startDate: Date | number, endDate: Date | number): number => {
    if (!startDate || !endDate)
        return 0;

    const interval = dateFns.differenceInSeconds(endDate, startDate);

    return Math.ceil(interval)
}

export const getDateOfTime = (date: number | string | Date, dateFormat: string | undefined = 'dd.MM.yyyy', withTime: string | undefined = 'dd.MM.yyyy HH:mm:ss') => {
    if (!date) return date;

    const isTimeExactlyTwelve = format(date, 'HH:mm:ss', false) === '00:00:00';

    if (isTimeExactlyTwelve)
        return format(date, dateFormat, false);

    return format(date, withTime, false)
}

export const getDurationInDaysAndHours = (start: number | Date, end: number | Date): string => {
    if (!start || !end)
        return '0 ч.';

    const duration = dateFns.intervalToDuration({ start, end });

    const minutes = (duration?.minutes ?? 0) + ((duration?.seconds ?? 0) > 30 ? 1 : 0);
    let hours = duration?.hours ?? 0;
    let days = duration?.days ?? 0;
    const months = duration?.months ?? 0;

    if (months) {
        days = days + (hours > 12 ? 1 : 0);
        return `${months}мес${days ? ` ${days}д` : ''}`
    }

    if (days) {
        hours = hours + (minutes > 30 ? 1 : 0);
        return `${days}д${hours ? ` ${hours}ч` : ''}`
    }

    if (hours) {
        return `${hours}ч${minutes ? ` ${minutes}мин` : ''}`
    }

    return `${Math.max(minutes, 1)}мин`
}

/**
 * Timstamp в секундах -> Date
 * @param timestamp 
 */
export const toDateTime = (timestamp: number) => {
    return new Date(timestamp * 1000);
}

export const toUnixTimestampSeconds = (date: Date) => {
    return Math.floor(date.getTime() / 1000);
}

export const addPeriod = (date: Date | number, period: DurationPeriod) => {
    return new Date(Number(date) + getTotalSeconds(period) * 1000);
}

type DurationPeriod = { days?: number, hours?: number, minutes?: number, seconds?: number }

export const getTotalSeconds = (period: DurationPeriod): number => {
    return (period.seconds ?? 0) +
        (period.minutes ?? 0) * 60 +
        (period.hours ?? 0) * 3600 +
        (period.days ?? 0) * 86400;
}

/**
 * Конвертирует дату и время в Unix-время в секундах, игнорируя часовой пояс
 * 
 * 08:00:00 GMT +4 => 12:00:00 GMT +0
 * @returns
 * timstamp в секундах
 */
export const toUnixTimestampSecondsFromView = (date: Date): number => {

    const utcTimestampMillis = Date.UTC(
        date.getFullYear(), date.getMonth(),
        date.getDate(), date.getHours(),
        date.getMinutes(), date.getSeconds());

    return Math.floor(utcTimestampMillis / 1000);
}

/**
 * Переводит дату из локального времени в дату без таймзоны.
 * @param localDateTime
 * @returns
 * 08:00:00 GMT +4 => 12:00:00 GMT +0
*/
export const fromViewDateTime = (localDateTime: Date): Date => {
    const timestampIgnoreTz = toUnixTimestampSecondsFromView(localDateTime);
    return toDateTime(timestampIgnoreTz);
}

/**
 * Возвращает скорректированную дату путем исключения временной зоны для правильного отображения.
 * Поскольку браузер автоматически преобразует время в местное.
 * 
 * Сервер функционирует вне зависимости от временной зоны,
 * и ожидается, что клиент работает в временной зоне сервера,
 * не зависимо от фактического местонахождения клиента.
 * @param dateOrTimestampSeconds дата или timstamp в секундах
 * @returns {Date} дата с вычтенной таймзоной
 */
export const toViewDateTime = (dateOrTimestampSeconds: Date | number) => {
    const date = typeof dateOrTimestampSeconds === 'number'
        ? new Date(dateOrTimestampSeconds * 1000)
        : new Date(dateOrTimestampSeconds);

    return setTimeZoneOffset(date, -planTimeZoneOffset);
}

//Отображает промежуток между текущей датой и датой-параметром,
//В зависимости от разницы между датами выводит результат
export const formatAsDateTimeFromNow = (strDate: string, locale: AllowedLocale = DefaultLocale) => {
    const date = new Date(strDate);
    const now = new Date();
    const diffInDays = dateFns.differenceInCalendarDays(date, now);

    switch (diffInDays) {
        case -1:
            return `вчера ${dateFns.format(date, 'HH:mm')}`;
        case 0:
            return `сегодня ${dateFns.format(date, 'HH:mm:ss')}`;
        default:
            return dateFns.format(date, 'd MMMM yyyy HH:mm', { locale: AllowedLocales[locale] });
    }
};

//Возвращает разницу между текущей датой и датой, приходящей в функцию.
//Результат - строка в формате "5 дней", "10 часов", "5 месяцев"
export const formatDistanceToNow = (strDate: string, locale: AllowedLocale = DefaultLocale) => {
    const date = new Date(strDate);
    return dateFns.formatDistanceToNow(date, { locale: AllowedLocales[locale] });
};

//Получает значение в процентах от 24 часов и возвращает результат: часы и минуты
//Для перевода начала и конца смены в календарях к удобному виду
export const formattedTime = (value: number): any => {
    if (typeof value !== 'number')
        return undefined;

    let minutes = 1440 * Number(value);
    let dt = new Date(+minutes.toFixed() * 60 * 1000);

    dt = dateFns.addMinutes(dt, dt.getTimezoneOffset());

    return {
        hours: dateFns.format(dt, 'HH'),
        minutes: dateFns.format(dt, 'mm'),
    };
};

export const getDaysQuantity = (seconds: number): number | undefined => {
    return Math.ceil(seconds / (60 * 60 * 24));
}

export const getWeekPeriod = (date: Date = new Date()) => ({
    start: dateFns.startOfISOWeek(date),
    end: dateFns.endOfISOWeek(date),
});

export const getCurrentMonthRange = () => {
    const date = new Date();
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    return [firstDay, lastDay];
}

const timeTrackingTimeParts = [
    { mu: 'day', code: 'd', min: 60 * 8 },
    { mu: 'day', code: 'д', min: 60 * 8 },
    { mu: 'hour', code: 'h', min: 60 },
    { mu: 'hour', code: 'ч', min: 60 },
    { mu: 'min', code: 'м', min: 1 },
    { mu: 'min', code: 'мин', min: 1 },
    { mu: 'min', code: 'm', min: 1 },
];

//Получает строковое значение времени,
//Проверяет части строки на соответствие формату, из константы timeTrackingTimeParts,
//Умножает числа коэффициент из константы и суммирует
//Возвращает количество минут
export const timeTrackingStrToMin = (str: string) => {
    if (!str)
        return 0;

    let result = 0;

    const distinctParts = {} as any;

    timeTrackingTimeParts.forEach(p => {
        if (!(p.mu in distinctParts)) {
            const partPattern = new RegExp(`\\d+\\s?${p.code}`, 'g');
            const match = str.match(partPattern);
            if (match) {
                distinctParts[p.mu] = true;
                result = result + p.min * parseInt(match[0].replace(p.code, ''), 10);
            }
        }
    });

    return result;
}

//Метод для получения даты начала периода. За несколько дней до даты, которую присылает пользователь.
//Например: пользователь присылает дату 25.04.2025 и количество дней = 5. Метод вернет 20.04.2025
export const getPreviousDate = (date: Date, daysRange: number) => {
    const prevDate = new Date(date);

    return new Date(prevDate.setDate(prevDate.getDate() - daysRange))
}
